import { LocaleLink } from '../../../i18n/locale-link';
import { formatCount } from '../../../lib/format';
import type { LandingCategory } from '../../../lib/landing-client';
import { LandingSection } from '../layout/landing-section';

interface Props {
  kicker: string;
  title: string;
  lead: string;
  categories: LandingCategory[];
  remaining: number;
  moreLabel: string;
  regionsLabel: string;
  regions: { code: string; label: string }[];
  onClick: (label: string) => void;
}

/**
 * «Ваша ниша уже в каталоге» — два ряда чипов: живые категории со счётчиком
 * (из `landing-stats`, обновляется вместе с каталогом) и статичный список
 * всех регионов из контент-файла.
 *
 * ⚠️ Код региона идёт в URL как есть, БЕЗ `toLowerCase()`: пререндер пишет
 * файлы под точным регистром кода (`/region/AND`), см. докстринг
 * `StartContent.regions` в content/landings/types.ts.
 */
const CHIP_CLS =
  'inline-flex min-h-[44px] items-center gap-1 rounded-full border border-edge bg-bone-surface px-3 py-2 text-[13px] font-bold text-ink no-underline shadow-m-sm transition hover:-translate-y-px hover:border-brand sm:min-h-0 sm:px-4 sm:py-2.5 sm:text-[14.5px]';

export function CategoryStrip({
  kicker,
  title,
  lead,
  categories,
  remaining,
  moreLabel,
  regionsLabel,
  regions,
  onClick,
}: Props) {
  return (
    <LandingSection id="niche" top="sm">
      <span className="mb-3 inline-block text-[12.5px] font-extrabold uppercase tracking-[0.08em] text-brand">
        {kicker}
      </span>
      <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
        {title}
      </h2>
      <p className="max-w-[680px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">{lead}</p>

      {/*
        Ширину чипа на телефоне задают ШРИФТ и БОКОВЫЕ отступы — их и режем,
        чтобы полоса читалась как компактная навигация, а не как список в
        столбик. Вертикальный отступ, наоборот, держим: `min-h-[44px]` — это
        тач-цель, и на количество чипов в ряду она не влияет.
      */}
      <div className="mt-[22px] flex flex-wrap gap-2 sm:gap-2.5">
        {categories.map((cat) => (
          <LocaleLink
            key={cat.slug}
            to={`/category/${cat.slug}`}
            onClick={() => onClick(`category-${cat.slug}`)}
            className={CHIP_CLS}
          >
            {cat.name} <b className="font-extrabold text-brand">{formatCount(cat.active)}</b>
          </LocaleLink>
        ))}
        {remaining > 0 && (
          <LocaleLink to="/catalog" onClick={() => onClick('all-categories')} className={CHIP_CLS}>
            {moreLabel}
          </LocaleLink>
        )}
      </div>

      <p className="mb-2.5 mt-[18px] text-sm font-extrabold text-ink">{regionsLabel}</p>
      <div className="flex flex-wrap gap-2.5">
        {regions.map((region) => (
          <LocaleLink
            key={region.code}
            to={`/region/${region.code}`}
            onClick={() => onClick(`region-${region.code}`)}
            className="inline-flex min-h-[44px] items-center rounded-full border border-edge bg-bone-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-soft no-underline shadow-m-sm transition hover:-translate-y-px hover:border-brand sm:min-h-0 sm:px-[13px] sm:py-[7px] sm:text-[13px]"
          >
            {region.label}
          </LocaleLink>
        ))}
      </div>
    </LandingSection>
  );
}
